"use client";

import Image from "next/image";
import { useRef, useState, type MouseEvent } from "react";

import type { RentalStatus } from "@/lib/rentals";

import { useImageFade } from "./use-image-fade";
import { VariantPill } from "./VariantPill";

type Props = {
  name: string;
  /** The variant this card is, shown as a pill beside the name. */
  variantLabel: string | null;
  manufacturer: string | null;
  summary: string | null;
  /** Showcase images, cover first; the card scrubs through up to 3. */
  images: string[];
  status: RentalStatus;
  soldLabel: string;
  comingSoonLabel: string;
};

/**
 * Square catalog cell.
 *
 * Resting: the cover sits behind a heavy bg-tinted dim (0.9), so the standard
 * fg text (manufacturer, name, summary) reads cleanly — it looks almost like a
 * text-only card with a faint photo behind it.
 *
 * On hover: the text hides and the dim lifts so the photo shows solid; moving
 * the cursor horizontally scrubs through the product's images (left→right =
 * image 1→N, up to 3) to spin through the angles. Cards with one image just
 * reveal it solid; text-only cards (no image) are unaffected.
 *
 * Status drives only the unavailability overlay; body text + photo render
 * identically across statuses. sold/coming_soon get a 2px diagonal X across
 * the card in the bg color (so it only shows over the photo on hover);
 * live cards have no overlay.
 */
export function RentalItemCard({
  name,
  variantLabel,
  manufacturer,
  summary,
  images,
  status,
  soldLabel,
  comingSoonLabel,
}: Props) {
  const scrubImages = images.slice(0, 3);
  const hasImage = scrubImages.length > 0;
  const canScrub = scrubImages.length > 1;

  const ref = useRef<HTMLElement>(null);
  const [hovering, setHovering] = useState(false);
  const [active, setActive] = useState(0);

  // Fade the photo in once the cover loads, rather than popping. Gated on the
  // cover so the fade plays once per card; the inner scrub swap stays instant.
  const {
    loaded: coverLoaded,
    ref: coverRef,
    onLoad: onCoverLoad,
  } = useImageFade();

  const isSold = status === "sold";
  const isComingSoon = status === "coming_soon";

  // Body hides only when a photo is there to reveal underneath.
  const bodyHidden = hovering && hasImage;

  function handleMove(e: MouseEvent<HTMLElement>) {
    if (!canScrub || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const idx = Math.min(
      scrubImages.length - 1,
      Math.max(0, Math.floor(ratio * scrubImages.length)),
    );
    if (idx !== active) setActive(idx);
  }

  return (
    <article
      ref={ref}
      onMouseEnter={() => setHovering(true)}
      onMouseMove={handleMove}
      onMouseLeave={() => {
        setHovering(false);
        setActive(0);
      }}
      // @container: the cell is square, so its width IS the height the text
      // has to fit in. The line budgets below read that, not the viewport —
      // the same card is 171px in a phone's 2-up grid and 340px in the
      // desktop 3-up, and a viewport breakpoint cannot tell them apart.
      className="@container relative aspect-square overflow-hidden bg-bg-muted group-focus-visible:outline group-focus-visible:-outline-offset-2 group-focus-visible:outline-2 group-focus-visible:outline-fg"
    >
      {hasImage && (
        <>
          {/* The whole photo layer fades in when the cover loads; the inner
              images keep their instant scrub swap (so hover scrubbing stays
              responsive). */}
          <div
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              coverLoaded ? "opacity-100" : "opacity-0"
            }`}
          >
            {scrubImages.map((src, i) => (
              <Image
                key={src}
                ref={i === 0 ? coverRef : undefined}
                src={src}
                alt={i === 0 ? name : ""}
                aria-hidden={i === 0 ? undefined : true}
                fill
                sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                onLoad={i === 0 ? onCoverLoad : undefined}
                className={`object-cover ${
                  isSold || isComingSoon ? "grayscale" : ""
                } ${i === active ? "opacity-100" : "opacity-0"}`}
                unoptimized
              />
            ))}
          </div>
          <div
            aria-hidden
            className="absolute inset-0 transition-opacity duration-200"
            style={{
              backgroundColor: "var(--color-bg)",
              opacity: hovering ? 0 : 0.9,
            }}
          />
        </>
      )}

      {(isSold || isComingSoon) && !hovering && (
        <>
          {/* Diagonal X overlay marks the card as unavailable at rest.
              Lines run corner-to-corner in the bg color so they read as
              a subtle darker trace against the bg-dimmed photo. Removed
              on hover so the (grayscaled) photo can be inspected cleanly.
              vectorEffect keeps the stroke at 2px regardless of size. */}
          <svg
            aria-hidden
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="pointer-events-none absolute inset-0 z-10 h-full w-full text-bg"
          >
            <line
              x1="0"
              y1="0"
              x2="100"
              y2="100"
              stroke="currentColor"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1="100"
              y1="0"
              x2="0"
              y2="100"
              stroke="currentColor"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <span className="sr-only">
            {isSold ? soldLabel : comingSoonLabel}
          </span>
        </>
      )}

      <div
        className="relative z-20 flex h-full flex-col p-5 transition-opacity duration-200 sm:p-6"
        style={{
          opacity: bodyHidden ? 0 : 1,
          pointerEvents: bodyHidden ? "none" : undefined,
        }}
      >
        {manufacturer && <p className="label">{manufacturer}</p>}
        <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {/* The name yields lines before the summary does: it sets the type
              and a long one would otherwise eat the whole cell. The detail
              page carries it whole. */}
          <h3 className="line-clamp-2 font-display text-xl tracking-tight text-fg @min-[15rem]:line-clamp-3 @min-[20rem]:line-clamp-4 sm:text-2xl">
            {name}
          </h3>
          {variantLabel && <VariantPill label={variantLabel} filled />}
        </div>
        {/* shrink-0 is the whole fix: as a shrinkable flex item in a fixed
            square, the summary was squeezed BELOW its clamp and its own
            overflow:hidden then cut the last line through the middle of the
            glyphs. It keeps its lines; the budget above is what gives. On the
            narrowest cell a pill costs a line of blurb — it wraps under a
            two-line name, and 131px of text height does not hold both. */}
        {summary && (
          <p
            className={`mt-2 shrink-0 text-xs text-fg-muted @min-[12rem]:line-clamp-2 @min-[15rem]:line-clamp-3 ${
              variantLabel ? "line-clamp-1" : "line-clamp-2"
            }`}
          >
            {summary}
          </p>
        )}
      </div>
    </article>
  );
}
